import {useState} from 'react';
import {Navigate} from 'react-router';
import {toast} from 'sonner';
import {useCurrentSystem} from '@/hooks/useCurrentSystem.tsx';
import {useGetCustomerFileQuery} from '@/store/customerFile.ts';
import {PageHeader} from '@/layout/components/PageHeader.tsx';
import {PageWrapper} from '@/layout/components/PageWrapper.tsx';
import {Spinner} from '@/components/ui/spinner.tsx';
import {CustomerFileKPIs, QuickActionsCard} from './components/CustomerFileKPIs.tsx';
import {CustomerFileInfoCard} from './components/CustomerFileInfoCard.tsx';
import {CustomerFileBillingCard} from './components/CustomerFileBillingCard.tsx';
import {CustomerFileTabs} from './components/CustomerFileTabs.tsx';
import type {CustomerFileTabValue} from './components/CustomerFileTabs.tsx';
import {ExclamationTriangleIcon, PlusIcon, XMarkIcon} from '@heroicons/react/24/outline';

export function CustomerFilePage() {
    const {role, system} = useCurrentSystem();

    if (role && role > 2) {
        toast.info('רק בעלים או מנהל יכולים לגשת לתיק הלקוח');
        return <Navigate to="/" replace />;
    }

    return <CustomerFilePageContent />;
}

function CustomerFilePageContent() {
    const {system: currentSystem} = useCurrentSystem();
    const {data, isLoading, isError} = useGetCustomerFileQuery(currentSystem?.id);
    const canEdit = data?.role === 1;
    const [activeTab, setActiveTab] = useState<CustomerFileTabValue>('systems');

    if (isLoading) {
        return (
            <PageWrapper>
                <PageHeader title="תיק לקוח" />
                <div className="flex justify-center py-20">
                    <Spinner size="lg" />
                </div>
            </PageWrapper>
        );
    }

    if (isError || !data) {
        return (
            <PageWrapper>
                <PageHeader title="תיק לקוח" />
                <div className="flex flex-col items-center gap-3 py-20 text-gray-500">
                    <ExclamationTriangleIcon className="w-10 h-10 text-gray-300" />
                    <p className="text-sm">לא נמצא תיק לקוח מקושר למערכת זו.</p>
                </div>
            </PageWrapper>
        );
    }

    const {customer, kpi} = data;

    const hasPendingInvoices = kpi.pending_invoices_count > 0;
    const pendingTotal = parseFloat(kpi.pending_invoices_total || '0');
    const isCreditType = customer.payment_type === 1 || customer.payment_type === 7;
    const noPaymentMethod = customer.payment_type === 0;
    const creditExpiringSoon = isCreditType && customer.expiration
        ? isExpiringSoon(customer.expiration)
        : false;
    const hasFailedPayment = kpi.last_failed_payment != null;

    const alerts: AlertItem[] = [];
    if (noPaymentMethod) alerts.push({type: 'warn', message: 'לא הוגדר אמצעי תשלום. נא להגדיר אמצעי תשלום כדי להמשיך בשימוש בשירות.'});
    if (hasFailedPayment && kpi.last_failed_payment) alerts.push({type: 'bad', message: `ניסיון החיוב האחרון נכשל (${kpi.last_failed_payment.date}${kpi.last_failed_payment.reason ? ` — ${kpi.last_failed_payment.reason}` : ''}). נא לעדכן פרטי תשלום.`});
    if (hasPendingInvoices && !hasFailedPayment) alerts.push({type: 'warn', message: `יש ${kpi.pending_invoices_count} חשבוניות ממתינות לתשלום בסך ₪${pendingTotal.toLocaleString('he-IL', {minimumFractionDigits: 0, maximumFractionDigits: 2})}.`});
    if (creditExpiringSoon) alerts.push({type: 'info', message: 'כרטיס האשראי עומד לפוג תוקפו בקרוב. נא לעדכן פרטי כרטיס.'});

    return (
        <PageWrapper>
            {/* Title row + inline alerts */}
            <div className="flex items-start justify-between gap-x-4 gap-y-3 flex-wrap">
                <div className="shrink-0">
                    <PageHeader title="תיק לקוח" />
                    <p className="text-sm text-gray-500 mt-0.5">{customer.business_name}</p>
                </div>
                {alerts.length > 0 && <AlertsInline alerts={alerts} />}
            </div>

            {/* Workspace: aside (right, fixed 340px) + main (flex-1) */}
            <div className="flex flex-col lg:flex-row gap-5 items-start">
                {/* RIGHT aside — quick actions + info + billing cards */}
                <aside className="w-full lg:w-[340px] lg:shrink-0 flex flex-col gap-4 order-2 lg:order-1">
                    <QuickActionsCard
                        systems={data.systems ?? []}
                        customer={customer}
                        currentSystemId={currentSystem?.id}
                        currentUserRole={data.role}
                    />
                    {/* md: info+billing side-by-side; lg: stacked back (fixed 340px aside) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
                        <CustomerFileInfoCard customer={customer} canEdit={canEdit} />
                        <CustomerFileBillingCard customer={customer} canEdit={canEdit} />
                    </div>
                </aside>

                {/* MAIN — KPIs + tabs */}
                <div className="flex-1 min-w-0 space-y-4 order-1 lg:order-2">
                    <CustomerFileKPIs kpi={kpi} paymentDay={customer.payment_day} onTabSelect={setActiveTab} />
                    <CustomerFileTabs data={data} canManage={canEdit} activeTab={activeTab} onTabChange={setActiveTab} />
                </div>
            </div>
        </PageWrapper>
    );
}

interface AlertItem {type: 'warn' | 'bad' | 'info'; message: string}

function AlertBanner({type, message}: AlertItem) {
    const styles = {
        warn: 'bg-amber-50 border-amber-200 text-amber-800',
        bad: 'bg-red-50 border-red-200 text-red-800',
        info: 'bg-blue-50 border-blue-200 text-blue-800',
    };
    return (
        <div className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${styles[type]}`}>
            <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
            <span className="leading-snug">{message}</span>
        </div>
    );
}

function AlertsInline({alerts}: {alerts: AlertItem[]}) {
    const [expanded, setExpanded] = useState(false);
    if (alerts.length === 0) return null;
    const first = alerts[0];
    const rest = alerts.slice(1);
    return (
        <div className="flex items-start gap-2 relative">
            <AlertBanner {...first} />
            {rest.length > 0 && (
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="inline-flex items-center gap-0.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full px-2 py-1 transition-colors mt-1"
                    >
                        {expanded ? <XMarkIcon className="w-3 h-3" /> : <PlusIcon className="w-3 h-3" />}
                        {expanded ? 'הסתר' : `${rest.length} נוספות`}
                    </button>
                    {expanded && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setExpanded(false)} />
                            <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-xl p-2 space-y-1.5 w-[min(320px,calc(100vw-1rem))]">
                                {rest.map((a, i) => (
                                    <AlertBanner key={i} {...a} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function isExpiringSoon(expiration: string): boolean {
    const exp = new Date(expiration);
    const now = new Date();
    const diffDays = (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays < 60;
}